import { Suchfeld, passtSuche } from '../teile/Schluesselsuche.tsx';
import { useEffect, useMemo, useState } from 'react';
import { api, datum, euro, heuteIso, zeitpunkt, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { extern } from '../teile/extern.ts';
import { Empfaengerwahl } from '../teile/Empfaengerwahl.tsx';
import { fragen, eingeben } from '../teile/rueckfrage.tsx';

/* GEBÜHREN BEZAHLEN (.258)
 * ============================================================================
 * ORIGINALTEXT MARVIN (30.09.2026)
 *   „eine eigene menü seite, da kommen die dateien aus den emails rein, sie
 *    werden aufgelistet, ich kann sie ansehen, und ich will sie direkt
 *    überweisen. prüfe mal, ob wir zu revolut business eine API schnittstelle
 *    angebunden bekommen ... anosonsten halt nur die auflistung und ich
 *    markiere dann da dass ich sie bezahlt habe."
 *
 * EINE KARTE JE BESCHEID. Was zum Bezahlen nötig ist, liest die Plattform aus
 * dem Bescheid; was fehlt oder nicht stimmt, steht rot da. Markieren, dann
 * „An Revolut": ein Entwurf, freigegeben wird in der Revolut-App. Ohne
 * Revolut: „Bezahlt" mit Datum. */

type Filter = 'offen' | 'ueber' | 'bald' | 'entwurf' | 'bezahlt' | 'erwartet' | 'alle';

const ART: Record<string, string> = {
  GEBUEHRENBESCHEID: 'Gebührenbescheid', VAO: 'Anordnung mit Gebühr', POLLER: 'Poller', AUSNAHMEGENEHMIGUNG: 'Ausnahmegenehmigung',
};

/** Die Prüfziffer der IBAN, wie auf dem Server (regeln/zahlung.ts). */
function ibanOk(roh: string): boolean {
  const iban = roh.replace(/[\s.\-]/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith('DE') && iban.length !== 22) return false;
  let rest = 0;
  for (const z of iban.slice(4) + iban.slice(0, 4)) {
    const w = /\d/.test(z) ? z : String(z.charCodeAt(0) - 55);
    for (const c of w) rest = (rest * 10 + Number(c)) % 97;
  }
  return rest === 1;
}

export default function Gebuehren({ ich, gehZu, oeffne }: { ich: Anmelder; gehZu?: (wo: string) => void; oeffne?: (id: string, reiter?: string) => void }) {
  const [d, setD] = useState<{ zeilen: any[]; summen: any; darf_bezahlen: boolean; revolut: any; ohne_auftrag?: any[]; erwartet?: any[]; nur_haken?: any[] } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('offen');
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [bearbeiten, setBearbeiten] = useState<any | null>(null);
  const [bezahlt, setBezahlt] = useState<string[] | null>(null);
  const [einrichten, setEinrichten] = useState(false);

  async function laden() {
    try { setD(await api.gebuehren()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => {
    laden();
    /* ZURÜCK VON REVOLUT: das Ergebnis steht in der Adresse und wird danach entfernt. */
    const q = new URLSearchParams(window.location.search);
    if (q.get('revolut') === 'verbunden') { setHinweis('Revolut ist verbunden. Jetzt noch das Konto wählen.'); setEinrichten(true); }
    if (q.get('revolut_fehler')) setFehler({ text: q.get('revolut_fehler')! } as Fehlerlage);
    if (q.has('revolut') || q.has('revolut_fehler')) {
      try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.hash); } catch { /* egal */ }
    }
  }, []);
  useFrisch(laden);

  const zeilen = d?.zeilen ?? [];
  const heute = heuteIso();
  const in7 = new Date(Date.now() + 7 * 864e5).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const passt = (z: any) => {
    switch (filter) {
      case 'offen': return z.zustand === 'offen';
      case 'ueber': return z.zustand === 'offen' && z.faellig_am && z.faellig_am < heute;
      case 'bald': return z.zustand === 'offen' && z.faellig_am && z.faellig_am >= heute && z.faellig_am <= in7;
      case 'entwurf': return z.zustand === 'entwurf';
      case 'bezahlt': return z.zustand === 'bezahlt' || z.zustand === 'vorher';
      case 'erwartet': return false;
      default: return true;
    }
  };
  /* WER SUCHT, SUCHT IN ALLEN (.300): KLS-ID, SM, Anschrift wie bei der
     Auftragssuche, dazu Gemeinde, Kassenzeichen, Verwendungszweck. Der
     Kachelfilter gilt dann nicht; der Zustand steht an jeder Karte. */
  const sucht = !!suche.trim();
  const sicht = useMemo(() => zeilen
    .filter((z) => sucht || passt(z))
    .filter((z) => passtSuche(suche, z.kls_id, z.sm_nummer, z.anschrift, z.empfaenger, z.gemeinde, z.kassenzeichen,
                              z.verwendungszweck, z.aktenzeichen, z.iban)),
  [d, filter, suche]);
  const auswahl = zeilen.filter((z) => gewaehlt.has(z.dokument_id));
  const summeAuswahl = auswahl.reduce((a, z) => a + (Number(z.betrag) || 0), 0);
  const kannRevolut = !!d?.revolut?.bereit && auswahl.length > 0 && auswahl.every((z) => z.zustand === 'offen' && z.bereit && !z.doppelt_zu);

  function umschalten(id: string) {
    setGewaehlt((g) => { const n = new Set(g); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  async function tu(schluessel: string, f: () => Promise<string | null>) {
    setLaeuft(schluessel); setFehler(null); setHinweis(null);
    try { const t = await f(); if (t) setHinweis(t); await laden(); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(null); }
  }

  const s = d?.summen;
  const kacheln: [Filter, string, any, string][] = [
    ['offen', 'offen', s?.offen, ''], ['ueber', 'überfällig', s?.ueberfaellig, 'rot'],
    ['bald', 'in 7 Tagen fällig', s?.bald, 'warnung'], ['entwurf', 'in Revolut', s?.entwurf, 'blau'],
    ['bezahlt', 'bezahlt diesen Monat', s?.bezahlt_monat, 'gruen'],
    /* BESCHEID ERWARTET (.302). Marvin: „das ist ein guter reminder."
       BLAU (.306): „wenn wir irgendwo auf einen gebührenbescheid, warten,
       kannst du das irgendwie besser kennzeichnen, zb in blau". */
    ['erwartet', 'Bescheid erwartet', { n: d?.erwartet?.length ?? 0 }, 'erwartet'],
  ];
  const erwartetSicht = (d?.erwartet ?? []).filter((e: any) =>
    passtSuche(suche, e.kls_id, e.sm_nummer, e.anschrift, e.gemeinde, e.aktenzeichen));
  const rv = d?.revolut;

  return (
    <div className="seite">
      <Kopf titel="Gebühren bezahlen" weg="Bescheide der Behörden aus den Mails"
            rechts={d?.darf_bezahlen ? (
              <div className="reihe" style={{ gap: 8, alignItems: 'center' }}>
                {rv?.bereit
                  ? <span className="merker gruen" title={rv.umgebung === 'sandbox' ? 'Testumgebung' : ''}>Revolut: {rv.konto_name}{rv.umgebung === 'sandbox' ? ' (Test)' : ''}{rv.saldo ? `, ${euro(rv.saldo.betrag)}` : ''}</span>
                  : <span className="merker">Revolut nicht verbunden</span>}
                {rv?.bereit && (
                  <button className="knopf klein" disabled={!!laeuft}
                          onClick={() => tu('abgleich', async () => {
                            const r = await api.gebuehrenAbgleichen();
                            return r.geprueft ? `${r.geprueft} Entwürfe geprüft, ${r.bezahlt} bezahlt, ${r.zurueck} wieder offen.` : 'Kein offener Entwurf bei Revolut.';
                          })}>Bei Revolut nachsehen</button>
                )}
                {/* DAS KONTO SAGT, WAS BEZAHLT IST, AUCH HIER (.269). Marvin: „hier bei
                    den gebühren bezahlen nicht die möglichkeit das konto anzugleichen." */}
                {rv?.bereit && (
                  <button className="knopf klein" disabled={!!laeuft}
                          onClick={() => tu('konto', async () => {
                            const r = await api.gebuehrenKontoabgleich();
                            const nz = (r as any).nicht_zugeordnet ?? [];
                            const ew = (r as any).entwuerfe;
                            return (ew?.geprueft ? `${ew.geprueft} Revolut-Entwürfe geprüft: ${ew.zurueck} storniert oder gelöscht, wieder offen; ${ew.bezahlt} ausgeführt. ` : '')
                              + `${r.umsaetze} Umsätze gelesen: ${r.gebuehren} Gebühren und ${r.bezahlt} Rechnungen als bezahlt markiert.`
                              + (nz.length ? ` Noch offen, obwohl eine Buchung das Kassenzeichen nennt (${nz.length}): `
                                + nz.slice(0, 8).map((x: any) => `${x.kassenzeichen}: ${x.grund}`).join('; ') + (nz.length > 8 ? ' …' : '') : '')
                              + ((r as any).ohne_buchung ? ` ${(r as any).ohne_buchung} offene Gebühren haben keine Buchung mit ihrem Kassenzeichen seit ${String((r as any).seit ?? '').split('-').reverse().join('.')}.` : '');
                          })}>{laeuft === 'konto' ? 'Liest das Konto …' : 'Konto abgleichen'}</button>
                )}
                {/* DER KONTOAUSZUG ALS CSV (.304). Marvin: „Sonst müssen wir es bei uns
                    exportieren ... Aber es muss funktionieren und valide Ergebnisse
                    liefern". Derselbe Abgleich mit den Zeilen der Revolut-Datei. */}
                <label className="knopf klein" style={{ cursor: 'pointer' }} title="Revolut-Kontoauszug als CSV (Transaktionen oder Kontoauszug)">
                  {laeuft === 'csv' ? 'Liest den Auszug …' : 'Kontoauszug (CSV) einlesen'}
                  <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} disabled={!!laeuft}
                         onChange={(e) => {
                           const f = e.target.files?.[0]; e.target.value = '';
                           if (!f) return;
                           tu('csv', async () => {
                             const r = await api.kontoauszugEinlesen(await f.text());
                             const nz = r.nicht_zugeordnet ?? [];
                             return `${r.umsaetze} Ausgaben aus „${f.name}" gelesen: ${r.gebuehren} Gebühren und ${r.bezahlt} Rechnungen als bezahlt markiert.`
                               + (nz.length ? ` Noch offen, obwohl eine Buchung das Kassenzeichen nennt (${nz.length}): `
                                 + nz.slice(0, 8).map((x: any) => `${x.kassenzeichen}: ${x.grund}`).join('; ') + (nz.length > 8 ? ' …' : '') : '');
                           });
                         }} />
                </label>
                <button className="knopf klein" onClick={() => setEinrichten(true)}>{rv?.bereit ? 'Revolut' : 'Revolut einrichten'}</button>
              </div>
            ) : undefined} />

      {/* DIE SUCHE STEHT OBEN UND BLEIBT STEHEN (.311). Marvin, 05.10.2026:
          „warum steht die suche nicht oben fixiert". Sie sucht in allen
          Gebühren und in den Papieren ohne Auftrag. */}
      <div className="reihe suchleiste" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12,
                                                   position: 'sticky', top: 'var(--eigenoben, var(--kopfhoehe, 58px))', zIndex: 4,
                                                   background: 'var(--grund)', padding: '8px 0', borderBottom: '1px solid var(--rand)' }}>
        <Suchfeld wert={suche} setze={setSuche} mehr="Gemeinde, Kassenzeichen, Datei" breit={280} />
        <button className={`knopf klein ${filter === 'alle' ? 'an' : ''}`} onClick={() => setFilter('alle')}>alle</button>
        {sucht && <span className="klein leise">sucht in allen Gebühren und den Papieren ohne Auftrag</span>}
      </div>

      {rv?.fehler && d?.darf_bezahlen && <div className="hinweis fehler klein">Revolut meldete{rv.fehler_am ? ` am ${zeitpunkt(rv.fehler_am)}` : ''}: {rv.fehler}. Der nächste gelungene Aufruf blendet das aus.</div>}

      <div className="freigabekacheln zahlkacheln">
        {kacheln.map(([f, t, w, farbe]) => (
          <button key={f} type="button" aria-pressed={filter === f}
                  className={`freigabekachel ${farbe} ${filter === f ? 'an' : ''}`} onClick={() => setFilter(f)}>
            <span className="zahl">{w?.n ?? 0}</span>
            <span>{t}</span>
            <span className="klein leise">{f === 'erwartet' ? 'Gebühr folgt' : euro(w?.summe ?? 0)}</span>
          </button>
        ))}
      </div>

      {/* DIE PRÜFLISTE BEHÖRDENPOST (.309) IST HIER WEG (.357). Marvin,
          14.10.2026: „Dann möchte ich diese Prüfliste entfernen." Papiere
          ohne Auftrag stehen weiter unten im eigenen Kasten. */}

      {/* DER KASTEN „NUR HAKEN AUS DER ALTEN LISTE" (.305) IST WEG (.331).
          Marvin, 07.10.2026: „der alte vermerk mit den 65 bescheiden kann
          weg." Die Zeilen gibt es weiter (nur_haken, für das Prüfwerkzeug);
          hier stehen sie nicht mehr. */}

      {/* NOCH OHNE AUFTRAG (.272, .305): ein Papier, das keinem Auftrag
          zugeordnet ist, hat noch keine Zeile. Es steht hier mit Datei,
          gelesener Gebühr und Grund, damit es nicht fehlt. */}
      {!!d?.ohne_auftrag?.length && (
        <div className="hinweis warnung">
          <b>{d.ohne_auftrag.length} Behördenpapier{d.ohne_auftrag.length === 1 ? '' : 'e'} noch keinem Auftrag zugeordnet.</b>{' '}
          Die Gebühr steht erst unter „offen", wenn das Papier im Posteingang an seinem Auftrag hängt (von selbst über
          KLS-ID, Anschrift oder Vorgangsnummer, sonst von Hand).
          <ul className="klein" style={{ margin: '6px 0 0 18px' }}>
            {d.ohne_auftrag.filter((e: any) => passtSuche(suche, e.betreff, e.dateiname, e.grund, e.absender)).slice(0, 20).map((e: any) => (
              <li key={e.id} style={{ marginBottom: 4 }}>
                <b>{e.betreff || e.dateiname}</b>, {datum(e.angekommen_am)}
                {e.gebuehr ? <>, Gebühr <b>{euro(e.gebuehr)}</b></> : null}
                {e.grund ? <span className="leise">, {e.grund}</span> : null}
                {' '}<button className="alslink" onClick={() => extern(api.eingangDateiAdresse(e.id, e.dateiname ?? undefined))}>Ansehen</button>
              </li>
            ))}
            {d.ohne_auftrag.length > 20 && <li>und {d.ohne_auftrag.length - 20} weitere</li>}
          </ul>
          {gehZu && <button className="knopf klein" style={{ marginTop: 6 }} onClick={() => gehZu('eingang')}>Im Posteingang zuordnen</button>}
        </div>
      )}

      <Karte titel={sucht ? `Suche in allen Gebühren (${sicht.length})` : filter === 'erwartet' ? `Bescheid erwartet (${erwartetSicht.length})` : filter === 'alle' ? `Alle Gebühren (${zeilen.length})` : `${kacheln.find((k) => k[0] === filter)?.[1] ?? ''} (${sicht.length})`}
             rechts={<button className={`knopf klein ${filter === 'alle' ? 'an' : ''}`} onClick={() => setFilter('alle')}>alle</button>}>
        {/* DIE ANTWORT STEHT IN DER LEISTE, DIE MAN SIEHT (.264), wie bei den Rechnungen. */}
        {((d?.darf_bezahlen && auswahl.length > 0) || fehler || hinweis) && (
          <div className="zahlleiste">
            {fehler && <div className="zahlmeldung"><Fehlerkasten lage={fehler} /></div>}
            {hinweis && <div className="zahlmeldung hinweis">✅ {hinweis} <button className="knopf klein" onClick={() => setHinweis(null)}>OK</button></div>}
            {d?.darf_bezahlen && auswahl.length > 0 && <>
            <span><b>{auswahl.length}</b> gewählt, <b>{euro(summeAuswahl)}</b></span>
            {rv?.saldo && summeAuswahl > rv.saldo.betrag && <span className="merker fehler">Nicht gedeckt: Kontostand {euro(rv.saldo.betrag)}</span>}
            <button className="knopf haupt" disabled={!kannRevolut || !!laeuft}
                    title={!rv?.bereit ? 'Revolut ist noch nicht eingerichtet.' : !kannRevolut ? 'Nur offene Gebühren mit vollständigen Angaben.' : ''}
                    onClick={() => tu('revolut', async () => {
                      const r = await api.gebuehrenAnRevolut([...gewaehlt]);
                      setGewaehlt(new Set());
                      return `In Revolut liegen ${r.anzahl} Entwürfe, je Bescheid eine Überweisung mit ihrem Kassenzeichen, zusammen ${euro(r.summe)}. Jetzt in der Revolut-App freigeben.`
                        + (r.uebersprungen.length ? ` Nicht dabei: ${r.uebersprungen.join('; ')}` : '');
                    })}>
              {laeuft === 'revolut' ? 'Einen Moment' : 'An Revolut (Entwurf)'}
            </button>
            <button className="knopf" disabled={!!laeuft || !auswahl.every((z) => z.zustand === 'offen' || z.zustand === 'vorher')}
                    onClick={() => setBezahlt([...gewaehlt])}>Als bezahlt markieren</button>
            <button className="knopf klein" onClick={() => setGewaehlt(new Set())}>Auswahl leeren</button>
            </>}
          </div>
        )}

        {/* DIE ANORDNUNGEN, DEREN GEBÜHRENBESCHEID NOCH FEHLT (.302). */}
        {(filter === 'erwartet' || (sucht && erwartetSicht.length > 0)) && (
          <div className="erwartetkasten" style={{ marginBottom: 12 }}>
            {sucht && <h3 className="klein" style={{ margin: '0 0 6px' }}>Bescheid erwartet ({erwartetSicht.length})</h3>}
            <p className="klein leise" style={{ marginTop: 0 }}>
              Anordnungen ohne eigene Gebühr, die „Anlagen: Gebührenbescheid" nennen. Sobald am Auftrag ein
              Gebührenbescheid liegt, verschwinden sie von selbst.
            </p>
            {!erwartetSicht.length ? <Leer text="Kein Gebührenbescheid steht aus." /> : (
              <div style={{ overflowX: 'auto' }}>
                <table className="liste klein">
                  <thead><tr><th>Baustelle</th><th>KLS</th><th>SM</th><th>Gemeinde</th><th>Anordnung</th><th>Eingang</th><th>wartet</th><th></th></tr></thead>
                  <tbody>
                    {erwartetSicht.map((e: any) => (
                      <tr key={e.id} className="erwartet">
                        <td><span className="merker erwartet" style={{ marginRight: 6 }}>Bescheid erwartet</span>{e.anschrift ?? ''}</td>
                        <td>{e.kls_id ?? ''}</td>
                        <td>{e.sm_nummer ?? ''}</td>
                        <td>{e.gemeinde ?? ''}</td>
                        <td>{e.aktenzeichen ?? ''}{e.gueltig_von ? <span className="leise">, {datum(e.gueltig_von)} bis {datum(e.gueltig_bis)}</span> : null}</td>
                        <td>{datum(e.eingegangen_am)}</td>
                        <td><span className={`merker ${e.tage > 30 ? 'fehler' : e.tage > 14 ? 'warnung' : ''}`}>{e.tage} Tage</span></td>
                        <td>{d?.darf_bezahlen && (
                          <button className="knopf klein" disabled={!!laeuft}
                                  onClick={async () => {
                                    const grund = await eingeben('Kein Gebührenbescheid zu dieser Anordnung? Grund (optional):', '');
                                    if (grund === null) return;
                                    tu('e' + e.id, async () => { await api.gebuehrErwartetErledigt(e.id, grund || undefined); return 'Abgehakt.'; });
                                  }}>kommt keiner</button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {d && !sicht.length && filter !== 'erwartet' && <Leer text={zeilen.length ? 'In diesem Filter ist nichts.' : 'Es ist noch kein Bescheid mit Gebühr eingegangen.'} />}
        <div className="freigabekarten">
          {sicht.map((z) => {
            const ueber = z.zustand === 'offen' && z.faellig_am && z.faellig_am < heute;
            const bald = !ueber && z.bald_faellig;
            const zustand = z.zustand === 'offen' ? (ueber ? 'überfällig' : 'offen')
              : z.zustand === 'entwurf' ? `in Revolut: ${z.bank_zustand ?? 'Entwurf'}`
              : z.zustand === 'bezahlt' ? `bezahlt am ${datum(z.bezahlt_am)}${z.bezahlt_wie === 'revolut' ? ' über Revolut' : ''}`
              : 'vor dem Start bezahlt';
            return (
              <div key={z.dokument_id} className={`freigabekarte zahlkarte ${z.zustand} ${gewaehlt.has(z.dokument_id) ? 'gewaehlt' : ''}`}>
                <div className="freigabekopf">
                  <label className="zahlwahl">
                    {d?.darf_bezahlen && (z.zustand === 'offen' || z.zustand === 'vorher') && (
                      <input type="checkbox" checked={gewaehlt.has(z.dokument_id)} onChange={() => umschalten(z.dokument_id)}
                             aria-label="Für die Zahlung wählen" style={{ marginTop: 4 }} />
                    )}
                    <div>
                      <div className="freigabefirma">{z.empfaenger ?? z.gemeinde ?? 'Empfänger nicht gelesen'}</div>
                      <div className="klein leise">{ART[z.art] ?? z.art}{z.gemeinde ? `, ${z.gemeinde}` : ''}, eingegangen {datum(z.eingegangen_am)}</div>
                    </div>
                  </label>
                  <div style={{ textAlign: 'right' }}>
                    <div className="freigabebetrag">{z.betrag ? euro(z.betrag) : '?'}</div>
                    {z.faellig_am && <div className={ueber ? 'merker fehler' : bald ? 'merker warnung' : 'klein leise'}>fällig {datum(z.faellig_am)}</div>}
                  </div>
                </div>
                <div className="klein" style={{ margin: '6px 0' }}>{z.anschrift ?? 'ohne Anschrift'}{z.kls_id ? `, KLS ${z.kls_id}` : ''}{z.sm_nummer ? `, SM ${z.sm_nummer}` : ''}
                  {/* WO DER BESCHEID AUF DER QNAP LIEGT (.266). */}
                  {z.nas_zustand === 'wartet' && <span className="leise">, QNAP: im Prüfordner, nach Prüfung und Zahlung in Belege</span>}
                  {z.nas_zustand === 'belege' && <span className="leise">, QNAP: in Belege</span>}
                  {z.nas_zustand === 'doppelt' && <span className="leise">, QNAP: als doppelt abgelegt (In Prüfung/Doppelt)</span>}
                  {/* GEPRÜFT (.294): laut Kontoumsatz bezahlt heißt noch nicht
                      angesehen. Erst mit diesem Knopf geht der Bescheid nach Belege. */}
                  {d?.darf_bezahlen && (z.zustand === 'bezahlt' || z.zustand === 'vorher') && !z.geprueft_am && !z.entwurf_von && z.bezahlt_wie !== 'hand' && (
                    <button className="knopf klein" style={{ marginLeft: 8 }} disabled={!!laeuft}
                            onClick={() => tu(z.dokument_id, async () => { await api.gebuehrenGeprueft([z.dokument_id]); return 'Geprüft. Der Bescheid geht nach Belege.'; })}>
                      Geprüft, nach Belege
                    </button>
                  )}
                  {!z.nas_zustand && z.nas_fehler && <span className="fehlertext">, QNAP: {z.nas_fehler}</span>}
                </div>
                <ul className="pruefliste">
                  <li className={z.empfaenger_bestaetigt && !z.iban_warnung ? 'ok' : 'nein'}>
                    <span className="zeichen" aria-hidden>{z.empfaenger_bestaetigt && !z.iban_warnung ? '✓' : '!'}</span>
                    <span><b>Empfänger:</b> {z.iban_warnung ? z.iban_warnung
                      : z.empfaenger_bestaetigt ? 'bestätigt' : z.iban_gueltig ? 'neu, einmal Name und IBAN prüfen und bestätigen' : 'ohne gültige IBAN'}
                      {d?.darf_bezahlen && z.zustand === 'offen' && z.iban_gueltig && !z.empfaenger_bestaetigt && z.empfaenger && (
                        <button className="knopf klein" style={{ marginLeft: 8 }} disabled={!!laeuft}
                                onClick={async () => { if (await fragen(`Empfänger bestätigen?\n\n${z.empfaenger}\n${z.iban_lesbar}\n\nNur bestätigen, wenn Name und IBAN mit dem Bescheid übereinstimmen.`))
                                  tu(z.dokument_id, async () => { await api.gebuehrEmpfaenger(z.dokument_id); return 'Empfänger bestätigt und gemerkt.'; }); }}>
                          Empfänger bestätigen
                        </button>
                      )}</span>
                  </li>
                  <li className={z.iban_gueltig ? 'ok' : 'nein'}>
                    <span className="zeichen" aria-hidden>{z.iban_gueltig ? '✓' : '!'}</span>
                    <span><b>IBAN:</b> {z.iban_lesbar ?? 'nicht gelesen'}{z.iban && !z.iban_gueltig ? ' (Prüfziffer falsch)' : ''}{z.bic ? `, ${z.bic}` : ''}
                      {/* BANKVERBINDUNG AKTUALISIEREN, an jeder Gebühr (.265). */}
                      {d?.darf_bezahlen && (z.zustand === 'offen' || z.zustand === 'vorher') && (
                        <button className="knopf klein" style={{ marginLeft: 8 }} onClick={() => setBearbeiten(z)}>Bankverbindung aktualisieren</button>
                      )}</span>
                  </li>
                  <li className={z.kassenzeichen && !z.kz_mehrfach ? 'ok' : 'nein'}>
                    <span className="zeichen" aria-hidden>{z.kassenzeichen && !z.kz_mehrfach ? '✓' : '!'}</span>
                    <span><b>Kassenzeichen:</b> {z.kassenzeichen ?? 'nicht gelesen'}</span>
                  </li>
                  <li className={z.verwendungszweck && !z.kz_mehrfach ? 'ok' : 'nein'}>
                    <span className="zeichen" aria-hidden>{z.verwendungszweck && !z.kz_mehrfach ? '✓' : '!'}</span>
                    <span><b>Verwendungszweck:</b> {z.verwendungszweck ?? 'fehlt'}</span>
                  </li>
                  {z.aktenzeichen && <li className="ok"><span className="zeichen" aria-hidden>✓</span><span><b>Aktenzeichen:</b> {z.aktenzeichen}</span></li>}
                  {/* KEIN KASSENZEICHEN (.306/.326): dieselbe Nummer an Bescheiden für verschiedene Baustellen, die nicht derselbe Bescheid sind. */}
                  {z.kz_mehrfach && <li className="nein"><span className="zeichen" aria-hidden>!</span><span><b>Kassenzeichen prüfen:</b> dieselbe Nummer steht auf Bescheiden für {z.kz_mehrfach} verschiedene Baustellen. Ein Kassenzeichen gehört zu genau einem Bescheid; das hier ist eher ein Zeichen der Behörde. Bitte im Bescheid nachsehen und unter „Bearbeiten" eintragen. Bis dahin wird nicht gezahlt und der Kontoabgleich ordnet hierüber nichts zu.</span></li>}
                  {/* DERSELBE BESCHEID AN MEHREREN PAPIEREN (.326): zählt und zahlt einmal; ist eines bezahlt, sind es alle. */}
                  {z.doppelt_zu && <li className="nein"><span className="zeichen" aria-hidden>!</span><span><b>Derselbe Bescheid:</b> gleiches Kassenzeichen wie ein anderes Papier (gleicher Auftrag, gleiche Baustelle oder gleiche Datei). Zählt und wird nur einmal bezahlt; ist das andere bezahlt, ist es dieses auch.</span></li>}
                </ul>
                {z.lesebefund && <div className="klein leise" style={{ marginTop: 4 }}>{z.lesebefund}</div>}
                {z.fehler && <div className="klein fehlertext" style={{ marginTop: 4 }}>{z.fehler}</div>}
                {z.notiz && <div className="klein" style={{ marginTop: 4 }}>Notiz: {z.notiz}</div>}
                <div className="freigabefuss">
                  <span className={`merker ${z.zustand === 'bezahlt' || z.zustand === 'vorher' ? 'gruen' : ueber ? 'fehler' : z.zustand === 'entwurf' ? 'blau' : ''}`}>{zustand}</span>
                  {z.bezug_id
                    ? <button className="knopf" onClick={() => extern(api.dateiAdresse(z.bezug_id, z.dateiname ?? undefined))}>Ansehen</button>
                    : <span className="merker warnung" title="Am Papier hängt keine Datei. In der Akte unter Dateien zuordnen.">keine Datei</span>}
                  {/* ZUR AKTE (.305): Marvin, „ich kann die gebührenbescheide nicht
                      öffnen auf dieser seite um überhaupt was gegenzuprüfen". */}
                  {oeffne && z.auftrag_id && <button className="knopf" onClick={() => oeffne(z.auftrag_id, 'dateien')}>Akte</button>}
                  {d?.darf_bezahlen && z.zustand !== 'entwurf' && z.zustand !== 'bezahlt' && (
                    <button className="knopf" onClick={() => setBearbeiten(z)}>Bearbeiten</button>
                  )}
                  {d?.darf_bezahlen && (z.zustand === 'offen' || z.zustand === 'vorher') && (
                    <button className="knopf" onClick={() => setBezahlt([z.dokument_id])}>Bezahlt …</button>
                  )}
                  {d?.darf_bezahlen && z.zustand === 'bezahlt' && z.bezahlt_wie === 'hand' && (
                    <button className="knopf klein" disabled={!!laeuft}
                            onClick={() => tu(z.dokument_id, async () => { await api.gebuehrOffen(z.dokument_id); return 'Wieder offen.'; })}>wieder offen</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Karte>

      {bearbeiten && <Bearbeiten z={bearbeiten} zu={() => setBearbeiten(null)} fertig={(t) => { setBearbeiten(null); setHinweis(t); laden(); }} />}
      {bezahlt && (
        <BezahltFenster anzahl={bezahlt.length} zu={() => setBezahlt(null)}
          los={async (tag) => { const ids = bezahlt; setBezahlt(null); await tu('bezahlt', async () => {
            const r = await api.gebuehrenBezahlt(ids, tag); setGewaehlt(new Set());
            return `${r.markiert} als bezahlt markiert.`;
          }); }} />
      )}
      {einrichten && d?.darf_bezahlen && <RevolutEinrichten stand={rv} zu={() => setEinrichten(false)} neu={laden} />}
    </div>
  );
}

function Bearbeiten({ z, zu, fertig }: { z: any; zu: () => void; fertig: (t: string) => void }) {
  const [w, setW] = useState({
    empfaenger: z.empfaenger ?? '', iban: z.iban_lesbar ?? '', bic: z.bic ?? '', kassenzeichen: z.kassenzeichen ?? '',
    verwendungszweck: z.verwendungszweck ?? '', faellig_am: z.faellig_am ?? '', betrag: z.betrag != null ? String(z.betrag).replace('.', ',') : '', notiz: z.notiz ?? '',
  });
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const setze = (k: keyof typeof w) => (v: string) => setW((a) => ({ ...a, [k]: v }));
  const ibanStimmt = !w.iban.trim() || ibanOk(w.iban);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      await api.gebuehrAendern(z.dokument_id, {
        ...w, betrag: w.betrag.trim() ? Number(w.betrag.replace(/\./g, '').replace(',', '.')) : null,
        faellig_am: w.faellig_am || null,
      });
      fertig('Gespeichert.');
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function nachlesen() {
    setLaeuft(true); setFehler(null);
    try { await api.gebuehrNachlesen(z.dokument_id); fertig('Neu gelesen. Von Hand eingetragene Angaben bleiben stehen.'); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel="Gebühr bearbeiten" untertitel={`${z.empfaenger ?? z.gemeinde ?? ''}, ${z.anschrift ?? ''}`} breite={720} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (ibanStimmt) speichern(); }}>
        <Fehlerkasten lage={fehler} />
        <div className="reihe" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Empfaengerwahl art="behoerde" wert={w.empfaenger} setze={setze('empfaenger')}
                          waehle={(e) => setW((a) => ({ ...a, empfaenger: e.name, iban: e.iban_lesbar, bic: e.bic ?? '' }))} />
          <Text name="betrag" titel="Betrag in €" wert={w.betrag} setze={setze('betrag')} hoechstens={12} breite={140} fehler={fehler}
                hinweis="Gilt auch in der LEB (10099980)." />
          <Text name="faellig_am" titel="Fällig am" art="date" wert={w.faellig_am} setze={setze('faellig_am')} breite={170} fehler={fehler} />
          <Text name="iban" titel="IBAN" wert={w.iban} setze={setze('iban')} hoechstens={40} breite={320} fehler={fehler}
                hinweis={w.iban.trim() ? (ibanStimmt ? 'Prüfziffer stimmt.' : 'Die Prüfziffer stimmt nicht.') : undefined} />
          <Text name="bic" titel="BIC (freiwillig)" wert={w.bic} setze={setze('bic')} hoechstens={20} breite={170} fehler={fehler} />
          <Text name="kassenzeichen" titel="Kassenzeichen" wert={w.kassenzeichen} setze={setze('kassenzeichen')} hoechstens={60} breite={240} fehler={fehler} />
          <Text name="verwendungszweck" titel="Verwendungszweck" wert={w.verwendungszweck} setze={setze('verwendungszweck')} hoechstens={140} breite={660} fehler={fehler}
                hinweis="So steht es bei der Kasse auf dem Kontoauszug. Das Kassenzeichen gehört an den Anfang." />
          <Text name="notiz" titel="Notiz" mehrzeilig wert={w.notiz} setze={setze('notiz')} hoechstens={1000} breite={660} fehler={fehler} />
        </div>
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={laeuft || !ibanStimmt}>{laeuft ? 'Einen Moment' : 'Speichern'}</button>
          <button type="button" className="knopf" disabled={laeuft} onClick={nachlesen}>Aus dem Bescheid neu lesen</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

function BezahltFenster({ anzahl, zu, los }: { anzahl: number; zu: () => void; los: (tag: string) => Promise<void> }) {
  const [tag, setTag] = useState(heuteIso());
  return (
    <Dialog titel={anzahl === 1 ? 'Als bezahlt markieren' : `${anzahl} Gebühren als bezahlt markieren`} breite={440} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (tag) los(tag); }}>
        <p style={{ marginTop: 0 }}>Für Zahlungen, die nicht über den Entwurf in Revolut liefen.</p>
        <Text name="tag" titel="Bezahlt am" art="date" wert={tag} setze={setTag} breite={200} />
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={!tag}>Markieren</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

/* DIE EINRICHTUNG IN VIER SCHRITTEN. Der Schlüssel entsteht auf dem Server und
   verlässt ihn nie; hier steht nur das Zertifikat, das nach Revolut gehört. */
function RevolutEinrichten({ stand, zu, neu }: { stand: any; zu: () => void; neu: () => Promise<void> }) {
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [zert, setZert] = useState<string | null>(stand?.zertifikat ?? null);
  const [client, setClient] = useState(stand?.client_id ?? '');
  const [umgebung, setUmgebung] = useState<'produktion' | 'sandbox'>(stand?.umgebung ?? 'produktion');
  const [konten, setKonten] = useState<{ id: string; name: string }[] | null>(null);
  const [kopiert, setKopiert] = useState<string | null>(null);
  async function tu(f: () => Promise<void>) {
    setLaeuft(true); setFehler(null);
    try { await f(); await neu(); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  useEffect(() => {
    if (stand?.verbunden) tu(async () => setKonten((await api.revolutKonten()).konten));
  }, [stand?.verbunden]);
  const kopieren = (t: string, was: string) => { navigator.clipboard?.writeText(t).then(() => setKopiert(was)).catch(() => {}); };
  const schritt = !zert && !stand?.schluessel_da ? 1 : !stand?.client_id ? 2 : !stand?.verbunden ? 3 : 4;

  return (
    <Dialog titel="Revolut Business verbinden" untertitel="Überweisungen als Entwurf, Freigabe in der Revolut-App" breite={760} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <ol className="revolutschritte">
        <li className={schritt > 1 ? 'fertig' : 'jetzt'}>
          <b>Schlüssel erzeugen.</b> Die Plattform erzeugt ihn selbst, er verlässt den Server nie. Einmal genügt: ein neuer Schlüssel macht das in Revolut eingetragene Zertifikat ungültig.
          <div className="reihe" style={{ gap: 8, marginTop: 6, alignItems: 'center' }}>
            <select value={umgebung} onChange={(e) => setUmgebung(e.target.value as any)} disabled={laeuft}>
              <option value="produktion">Echtes Konto</option>
              <option value="sandbox">Testumgebung (Sandbox)</option>
            </select>
            <button className="knopf" disabled={laeuft}
                    onClick={async () => { if (!stand?.schluessel_da || await fragen('Ein neuer Schlüssel trennt die bestehende Verbindung. Weiter?'))
                      tu(async () => setZert((await api.revolutSchluessel(umgebung)).zertifikat)); }}>
              {stand?.schluessel_da ? 'Neuen Schlüssel erzeugen' : 'Schlüssel erzeugen'}
            </button>
          </div>
        </li>
        <li className={schritt === 2 ? 'jetzt' : schritt > 2 ? 'fertig' : ''}>
          <b>In Revolut eintragen.</b> In Revolut Business: Einstellungen, API, „Zertifikat hinzufügen". Die 3 Felder dort, in derselben Reihenfolge:
          <div className="revolutkasten">
            <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="klein"><b>Zertifikatstitel</b> (frei wählbar, höchstens 20 Zeichen)</span>
              <button className="knopf klein" onClick={() => kopieren('MDS FTTH', 'titel')}>{kopiert === 'titel' ? 'kopiert' : 'kopieren'}</button>
            </div>
            <pre>MDS FTTH</pre>
          </div>
          <div className="revolutkasten">
            <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="klein"><b>OAuth-URI umleiten</b></span>
              <button className="knopf klein" onClick={() => kopieren(stand?.rueckweg ?? '', 'weg')}>{kopiert === 'weg' ? 'kopiert' : 'kopieren'}</button>
            </div>
            <pre>{stand?.rueckweg}</pre>
          </div>
          {zert && (
            <div className="revolutkasten">
              <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="klein"><b>Öffentlicher X509-Schlüssel</b> ({zert.length} von höchstens 2000 Zeichen, ganz einfügen)</span>
                <button className="knopf klein" onClick={() => kopieren(zert, 'zert')}>{kopiert === 'zert' ? 'kopiert' : 'kopieren'}</button>
              </div>
              <pre>{zert}</pre>
            </div>
          )}
          <p className="klein leise" style={{ margin: '4px 0 8px' }}>Die Warnung von Revolut gilt fremden Leuten, die um Zugang bitten. Hier ist es die eigene Plattform; der Schlüssel liegt nur auf dem Server.</p>
          Danach zeigt Revolut die <b>Client-ID</b>. Hier eintragen:
          <form className="reihe" style={{ gap: 8, marginTop: 6, alignItems: 'flex-end' }}
                onSubmit={(e) => { e.preventDefault(); tu(async () => { const r = await api.revolutClient(client.trim()); window.open(r.zustimmung, '_blank', 'noopener'); }); }}>
            <Text name="client_id" titel="Client-ID" wert={client} setze={setClient} hoechstens={200} breite={420} fehler={fehler} />
            <button className="knopf haupt" disabled={laeuft || client.trim().length < 8}>Speichern und Zugriff erlauben</button>
          </form>
        </li>
        <li className={schritt === 3 ? 'jetzt' : schritt > 3 ? 'fertig' : ''}>
          <b>Zugriff erlauben.</b> Revolut fragt nach der Zustimmung (mit dem Handy). Die Schalter dort so stellen:
          <ul className="klein" style={{ margin: '4px 0' }}>
            <li><b>Deine Kontodaten lesen:</b> an (Kontostand, Umsätze, Empfänger)</li>
            <li><b>Verwalte deine Kontodaten:</b> an (Empfänger und Überweisungsentwürfe anlegen)</li>
            <li><b>Zahlungen ausführen:</b> aus (freigegeben wird immer in der App)</li>
            <li><b>Lies sensible Kartendaten:</b> aus</li>
          </ul>
          Danach kommst du hierher zurück.
          {stand?.client_id && stand?.verbunden && (
            <div style={{ marginTop: 6 }}>
              <button className="knopf klein" disabled={laeuft}
                      onClick={() => tu(async () => { const r = await api.revolutZustimmung(); window.open(r.zustimmung, '_blank', 'noopener'); })}>
                Zugriff neu erteilen
              </button>
              <span className="klein leise" style={{ marginLeft: 8 }}>Falls die Schalter anders standen.</span>
            </div>
          )}
          {stand?.client_id && !stand?.verbunden && (
            <div style={{ marginTop: 6 }}>
              <button className="knopf" disabled={laeuft}
                      onClick={() => tu(async () => { const r = await api.revolutZustimmung(); window.open(r.zustimmung, '_blank', 'noopener'); })}>
                Zustimmung bei Revolut öffnen
              </button>
            </div>
          )}
        </li>
        <li className={schritt === 4 ? 'jetzt' : ''}>
          <b>Konto wählen.</b> Aus diesem Konto gehen die Überweisungen.
          {stand?.verbunden && (
            <div className="reihe" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {(konten ?? []).map((k) => (
                <button key={k.id} className={`knopf ${stand?.konto_id === k.id ? 'haupt' : ''}`} disabled={laeuft}
                        onClick={() => tu(async () => { await api.revolutKonto(k.id); })}>{k.name}</button>
              ))}
              {konten && !konten.length && <span className="klein">Kein aktives EUR-Konto gefunden.</span>}
            </div>
          )}
        </li>
      </ol>
      {stand?.bereit && <div className="hinweis">✅ Verbunden mit <b>{stand.konto_name}</b>{stand.verbunden_am ? ` seit ${datum(stand.verbunden_am)}` : ''}.</div>}
      <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
        <button className="knopf" onClick={zu}>Schließen</button>
        {stand?.verbunden && (
          <button className="knopf" disabled={laeuft}
                  onClick={async () => { if (await fragen('Die Verbindung zu Revolut trennen?')) tu(async () => { await api.revolutTrennen(); setKonten(null); }); }}>
            Verbindung trennen
          </button>
        )}
      </div>
    </Dialog>
  );
}

